import React, { useState, useEffect } from "react";
import { Modal as RBModal, Form, Button, Row, Col, FloatingLabel } from 'react-bootstrap';
import { API_ENDPOINTS } from './config/api';

const rotationOptions = [
  "General Surgery", "Colorectal Surgery", "Pediatric Surgery", "Oncologic Surgery",
  "Hepato-Biliary Surgery", "Elective Surgery", "Acute Care Surgery", "Burn Surgery",
  "Hernia Surgery", "Trauma Surgery", "Bariatric Surgery", "Surgical ICU",
  "HENT Surgery", "Renal Surgery", "Transplant Surgery"
];

const populationOptions = ["Pediatric", "Teen", "Adult", "Senior"];

const roleOptions = ["Chief Resident", "Primary Resident", "Secondary Resident", "Intern"];

const AddCaseLogModal = ({ isOpen, onClose, onSubmit, editingLog, getRotationForDate, attendingsFromParent, rotationSchedulesLoaded, residentDataLoaded }) => {
  const [form, setForm] = useState({
    procedureName: "",
    institution: "",
    patientPopulation: "",
    encounterNumber: "",
    date: "",
    rotation: "",
    attending: "",
    role: "",
    robotic: false,
    minimallyInvasive: false,
    pgyLevel: ""
  });

  const [attendings, setAttendings] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  

  
  const [newAttending, setNewAttending] = useState("");
  const [newProcedure, setNewProcedure] = useState("");
  const [newInstitution, setNewInstitution] = useState("");

  // Track if rotation is auto-assigned
  const [autoRotation, setAutoRotation] = useState(null);

  // Use attendings from parent if provided, otherwise use local state
  const attendingsList = (attendingsFromParent && attendingsFromParent.length > 0) ? attendingsFromParent : attendings;
  
  // Debug logging
  console.log('AddCaseLogModal - attendingsFromParent:', attendingsFromParent);
  console.log('AddCaseLogModal - attendings:', attendings);
  console.log('AddCaseLogModal - attendingsList:', attendingsList);

  useEffect(() => {
    // Load saved data from localStorage with program-specific keys
    const programId = localStorage.getItem('programId');
    const savedAttendings = localStorage.getItem(`savedAttendings_${programId}`);
    const savedProcedures = localStorage.getItem(`savedProcedures_${programId}`);
    const savedInstitutions = localStorage.getItem(`savedInstitutions_${programId}`);
    
    if (savedAttendings) setAttendings(JSON.parse(savedAttendings));
    if (savedProcedures) setProcedures(JSON.parse(savedProcedures));
    if (savedInstitutions) setInstitutions(JSON.parse(savedInstitutions));
    
    // Clear old localStorage data to prevent cross-program contamination
    if (programId) {
      localStorage.removeItem('savedAttendings');
      localStorage.removeItem('savedProcedures');
      localStorage.removeItem('savedInstitutions');
    }
  }, []);

  useEffect(() => {
    if (editingLog) {
      setForm({
        ...editingLog,
        date: editingLog.date.split('T')[0]
      });
      setNewAttending(editingLog.attending);
      setNewProcedure(editingLog.procedureName);
      setNewInstitution(editingLog.institution);
    } else {
      const fetchResidentData = async () => {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");

        if (!token || !userId) {
          console.error("No token or user ID found");
          return;
        }

        try {
          const response = await fetch(`${API_ENDPOINTS.RESIDENT}/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            setForm(prev => ({ ...prev, pgyLevel: data.year || "" }));
          }
        } catch (error) {
          console.error("Error fetching resident data:", error);
        }
      };

      if (isOpen) {
        fetchResidentData();
      }
    }
  }, [isOpen, editingLog]);

  // Function to fetch PGY level for a specific date
  const fetchPGYLevelForDate = async (date) => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!token || !userId || !date) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.PROMOTIONS}/residents/${userId}/pgy-from-history?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.pgyLevel) {
          setForm(prev => ({ ...prev, pgyLevel: data.pgyLevel }));
        }
      }
    } catch (error) {
      console.error("Error fetching PGY level for date:", error);
    }
  };

  useEffect(() => {
    console.log('Date changed in modal:', form.date);
    console.log('getRotationForDate function:', getRotationForDate);
    console.log('rotationSchedulesLoaded:', rotationSchedulesLoaded);
    console.log('residentDataLoaded:', residentDataLoaded);
    
    if (form.date && getRotationForDate && typeof getRotationForDate === 'function' && rotationSchedulesLoaded && residentDataLoaded) {
      console.log('Calling getRotationForDate with:', form.date);
      const auto = getRotationForDate(form.date);
      console.log('Auto rotation result:', auto);
      setAutoRotation(auto);
      if (auto) {
        console.log('Setting rotation to:', auto);
        setForm(prev => ({ ...prev, rotation: auto }));
      }
    } else {
      console.log('getRotationForDate not available, not a function, rotation schedules not loaded, or resident data not loaded');
    }
    
    // Fetch PGY level for the selected date
    if (form.date) {
      fetchPGYLevelForDate(form.date);
    }
  }, [form.date, getRotationForDate, rotationSchedulesLoaded, residentDataLoaded]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };
      
      // Implement mutual exclusivity for robotic and minimally invasive
      if (field === 'robotic' && value === true) {
        newForm.minimallyInvasive = false;
      } else if (field === 'minimallyInvasive' && value === true) {
        newForm.robotic = false;
      }
      
      return newForm;
    });
  };

  // Generic handler for dropdown fields
  const createDropdownHandler = (setNewValue, setShowDropdown, field) => (value) => {
    setNewValue(value);
    setShowDropdown(true);
    handleChange(field, value);
  };

  const createBlurHandler = (setShowDropdown) => () => {
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const createKeyDownHandler = (newValue, items, setItems, localStorageKey, setShowDropdown) => (e) => {
    if (e.key === 'Enter' && newValue.trim()) {
      const updatedItems = [...new Set([...items, newValue.trim()])];
      setItems(updatedItems);
      localStorage.setItem(localStorageKey, JSON.stringify(updatedItems));
      setShowDropdown(false);
    }
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!userId) {
      alert("User ID not found. Please log in again.");
      return;
    }

    // Save new values if they're not in the lists
    const programId = localStorage.getItem('programId');
    if (newAttending.trim() && !attendings.includes(newAttending.trim())) {
      const updatedAttendings = [...new Set([...attendings, newAttending.trim()])];
      setAttendings(updatedAttendings);
      localStorage.setItem(`savedAttendings_${programId}`, JSON.stringify(updatedAttendings));
    }

    if (newProcedure.trim() && !procedures.includes(newProcedure.trim())) {
      const updatedProcedures = [...new Set([...procedures, newProcedure.trim()])];
      setProcedures(updatedProcedures);
      localStorage.setItem(`savedProcedures_${programId}`, JSON.stringify(updatedProcedures));
    }

    if (newInstitution.trim() && !institutions.includes(newInstitution.trim())) {
      const updatedInstitutions = [...new Set([...institutions, newInstitution.trim()])];
      setInstitutions(updatedInstitutions);
      localStorage.setItem(`savedInstitutions_${programId}`, JSON.stringify(updatedInstitutions));
    }

    const url = editingLog 
      ? `${API_ENDPOINTS.RESIDENT}/case-logs/${editingLog.id}`
      : `${API_ENDPOINTS.RESIDENT}/case-logs`;

    const method = editingLog ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        ...form,
        residentId: parseInt(userId)
      }),
    });

    if (response.ok) {
      alert(editingLog ? "Case log updated" : "Case log added");
      onSubmit();
    } else {
      const errorData = await response.json();
      alert(`Failed to ${editingLog ? 'update' : 'add'} case: ${errorData.message || 'Unknown error'}`);
    }
  };

  return (
    <RBModal show={isOpen} onHide={onClose} centered size="lg">
      <RBModal.Header className="px-4 border-bottom-0" closeButton>
        <RBModal.Title className="w-50"><div className="d-flex flex-column justify-content-start align-items-start">
            <span>{editingLog ? "Edit Case Log" : "Add Case Log"}</span>
            <div className="text-hl"></div>
          </div>
        </RBModal.Title>
      </RBModal.Header>
      <RBModal.Body className="px-4">
        <Form>
           <Form.Group className="mb-3">
            <FloatingLabel label="Procedure Name" controlId="floatingInput" className="required-field">
            <Form.Control
              type="text"
              value={newProcedure}
              onChange={e => { setNewProcedure(e.target.value); handleChange('procedureName', e.target.value); }}
              placeholder="Enter or search..."
              required
            />
            </FloatingLabel>
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Institution" controlId="floatingInput">
                <Form.Control
                  type="text"
                  value={newInstitution}
                  onChange={e => { setNewInstitution(e.target.value); handleChange('institution', e.target.value); }}
                  placeholder="e.g., UNMC"
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Encounter # / MRN" controlId="floatingInput">
                <Form.Control
                  type="text"
                  value={form.encounterNumber}
                  onChange={e => handleChange('encounterNumber', e.target.value)}
                  placeholder="Enter encounter number"
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Date of Procedure" controlId="floatingInput" className="required-field">
                <Form.Control
                  type="date"
                  value={form.date}
                  onChange={e => handleChange('date', e.target.value)}
                  required
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Rotation" controlId="floatingInput">
                {rotationSchedulesLoaded && residentDataLoaded ? (
                  autoRotation ? (
                    <Form.Control type="text" value={autoRotation} readOnly />
                  ) : (
                    <Form.Select
                      value={form.rotation}
                      onChange={e => handleChange('rotation', e.target.value)}
                    >
                      <option value="">Select Rotation</option>
                      {rotationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                      
                    </Form.Select>
                  )
                ) : (
                  <Form.Control type="text" value="Loading rotations..." readOnly />
                )}
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Patient Population" controlId="floatingInput">
                <Form.Select
                  value={form.patientPopulation}
                  onChange={e => handleChange('patientPopulation', e.target.value)}
                >
                  <option value="">Select Population</option>
                  {populationOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <FloatingLabel label="Attending" controlId="floatingInput">
            <Form.Control
              type="text"
              value={newAttending}
              onChange={e => { 
                setNewAttending(e.target.value); 
                handleChange('attending', e.target.value); 
              }}
              onFocus={() => console.log('Attending field focused, available attendings:', attendingsList)}
              placeholder="Dr. Smith"
              list="attending-list"
              autoComplete="off"
            />
            </FloatingLabel>
            <datalist id="attending-list">
              {attendingsList.length === 0 ? (
                <option value="No attendings yet. Type to add." />
              ) : (
                attendingsList.map(att => (
                  <option key={att} value={att} />
                ))
              )}
            </datalist>
            {attendingsList.length === 0 && (
              <Form.Text className="text-muted">
                No attendings found. Type a name to add your first attending.
              </Form.Text>
            )}

            {/*{attendingsList.length > 0 && (
              <Form.Text className="text-muted">
                Available attendings: {attendingsList.join(', ')}
              </Form.Text>
            )}*/}
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="PGY Year" controlId="floatingInput">
                <Form.Control
                  type="text"
                  value={form.pgyLevel}
                  readOnly
                  disabled={!!form.pgyLevel}
                />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <FloatingLabel label="Role" controlId="floatingInput">
                <Form.Select
                  value={form.role}
                  onChange={e => handleChange('role', e.target.value)}
                >
                  <option value="">Select Role</option>
                  {roleOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Surgical Approach</Form.Label>
                <div className="d-flex gap-4">
                  <Form.Check
                    type="checkbox"
                    label="Robotic"
                    checked={form.robotic}
                    onChange={e => handleChange('robotic', e.target.checked)}
                    className="me-3"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Min.Invasive"
                    checked={form.minimallyInvasive}
                    onChange={e => handleChange('minimallyInvasive', e.target.checked)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </RBModal.Body>
      <RBModal.Footer className="px-4 border-top-0">
        <Button variant="light" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>Save Case</Button>
      </RBModal.Footer>
    </RBModal>
  );
};

export default AddCaseLogModal;
